"use client";

/**
 * Single-select card list used for graph-driven prompt responses.
 *
 * Chrome and description placement live in IntakeOptionCard; this component
 * only maps a radio-list item onto it.
 */

import { type ReactNode, useId } from "react";
import {
  IntakeOptionCard,
  IntakeOptionInputNameProvider,
} from "./IntakeOptionCard";

export interface RadioListItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  matchingProducts?: string[];
}

export interface IntakeRadioListProps {
  items: RadioListItem[];
  selectedIds: string[];
  /** Called with the id of the item that was clicked. */
  onToggle: (id: string) => void;
  /** Accessible name for the native radio group. */
  ariaLabel: string;
  /** Rendered inside the group after the cards — the refine row. */
  trailing?: ReactNode;
}

export function IntakeRadioList({
  items,
  selectedIds,
  onToggle,
  ariaLabel,
  trailing,
}: IntakeRadioListProps) {
  const inputName = `intake-radio-${useId()}`;

  return (
    <IntakeOptionInputNameProvider name={inputName}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className="flex flex-col gap-2"
      >
        {items.map((item) => (
          <IntakeOptionCard
            key={item.id}
            name={item.name}
            description={item.description}
            secondaryLabel={item.category || undefined}
            indicator="radio"
            selected={selectedIds.includes(item.id)}
            onSelect={() => onToggle(item.id)}
            footer={
              item.matchingProducts && item.matchingProducts.length > 0 ? (
                <>
                  <span className="mt-2 block border-t border-(--border-subtle)" />
                  <span className="mt-2 text-[12.5px] text-(--fg-muted)">
                    Addressed by:{" "}
                    <span className="font-semibold text-(--fg-secondary)">
                      {item.matchingProducts.join(", ")}
                    </span>
                  </span>
                </>
              ) : undefined
            }
          />
        ))}
        {trailing}
      </div>
    </IntakeOptionInputNameProvider>
  );
}
